import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Column,
	compareBooleansDesc,
	compareObjects,
	Conditional,
	Else,
	If,
	jsxJoin,
	omitKeys,
	Rule,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { EmptyView } from "../../../../../Components/Empty";
import { Icon } from "../../../../../Components/Icon";
import { SearchField } from "../../../../../Components/Search";
import {
	Student,
	StudentFilterInput,
	User,
	UserFilterInput,
	useStudentGuardianAndTeachersForMessagesQuery,
} from "../../../../../Utilities";
import { UserFilterFormState, UserFilterFormUtils } from "../../../../Filter";
import {
	SelectRecipientSection,
	splitRecipientsByRole,
	splitRecipientsByRoleForStudent,
	splitUserGroups,
	TopicStudentRecipientSection,
	UserGroupRecipientSection,
	UserRecipientList,
	UserRecipientSection,
} from "../Recipients";
import { useRecipients, useStudentTopicRecipients } from "./utils";
import styles from "./recipients.module.css";

const adminVariantStudentFilterInput: StudentFilterInput = {
	// TODO filtering by term
	hasStudentAcademicTermEnrollments: true,
};

const teacherVariantStudentFilterInput: StudentFilterInput = {
	// TODO filter by term
	or: [
		{
			studentHomeRoomSectionEnrollments: [
				{
					homeRoomSection: [
						{
							teacherHomeRoomSectionEnrollments: [
								{
									teacher: [
										{
											person: [
												{
													user: [
														{
															isCurrentEQ: true,
														},
													],
												},
											],
										},
									],
								},
							],
						},
					],
				},
			],
		},
		{
			studentCourseSectionEnrollments: [
				{
					courseSection: [
						{
							teacherCourseSectionEnrollments: [
								{
									teacher: [
										{
											person: [
												{
													user: [
														{
															isCurrentEQ: true,
														},
													],
												},
											],
										},
									],
								},
							],
						},
					],
				},
			],
		},
	],
};

const teacherVariantUserFilterInput: UserFilterInput = {
	person: [
		{
			or: [
				{
					hasEmployee: true,
				},
				{
					guardian: [
						{
							relationships: [
								{
									student: [teacherVariantStudentFilterInput],
								},
							],
						},
					],
				},
			],
		},
	],
};

export type MessageRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
	onClickUserGroupRecipient: (id: User["id"]) => void;
	selectedUserGroupIDs?: User["id"][];
	onClickTopicStudent: (id: Student["id"]) => void;
	selectedTopicStudentIDs?: Student["id"][];
	variant: "admin" | "teacher";
	withSelected?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function MessageRecipientSections({
	onClickUserRecipient,
	selectedUserIDs,
	onClickUserGroupRecipient,
	selectedUserGroupIDs,
	onClickTopicStudent,
	selectedTopicStudentIDs,
	variant,
	withSelected = false,
	className,
	style,
}: MessageRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const userFilterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	let finalUserFilterInput = omitKeys(userFilterInput, ["search"]);
	if (variant === "teacher") {
		finalUserFilterInput = {
			and: [userFilterInput, teacherVariantUserFilterInput],
		};
	}
	const { loading: loadingRecipients, data: recipientData } = useRecipients(
		userFilterInput.search,
		finalUserFilterInput,
	);
	const { data: allRecipientData } = useRecipients("", finalUserFilterInput);
	const { loading: loadingStudents, data: studentData } = useStudentTopicRecipients(
		userFilterInput.search,
		variant === "teacher" ? teacherVariantStudentFilterInput : adminVariantStudentFilterInput,
	);
	const { data: allStudentData } = useStudentTopicRecipients(
		"",
		variant === "teacher" ? teacherVariantStudentFilterInput : adminVariantStudentFilterInput,
	);
	const isLoading = loadingRecipients || loadingStudents;
	const allUsers = React.useMemo(
		() => allRecipientData?.userConnection.edges.map((e) => e.node) ?? [],
		[allRecipientData],
	);
	const { guardians, teachers, administration } = React.useMemo(
		() => splitRecipientsByRole(recipientData),
		[recipientData],
	);
	const allUserGroups = React.useMemo(
		() => allRecipientData?.userGroupConnection.edges.map((e) => e.node) ?? [],
		[allRecipientData],
	);
	const { viewerGroups, otherGroups } = React.useMemo(() => splitUserGroups(recipientData), [recipientData]);
	const students = React.useMemo(() => studentData?.studentConnection.edges.map((e) => e.node) ?? [], [studentData]);
	const allStudents = React.useMemo(
		() => allStudentData?.studentConnection.edges.map((e) => e.node) ?? [],
		[allStudentData],
	);

	const sections = [];
	if (students.length > 0) {
		sections.push(
			<TopicStudentRecipientSection
				key="Students"
				name="Students"
				students={students}
				onClickStudent={onClickTopicStudent}
				selectedTopicStudentIDs={selectedTopicStudentIDs}
			/>,
		);
	}
	if (guardians.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Parents & Guardians"
				name="Parents & Guardians"
				users={guardians}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}
	if (viewerGroups.length > 0) {
		sections.push(
			<UserGroupRecipientSection
				key="Your Groups"
				name="Your Groups"
				userGroups={viewerGroups}
				onClickRecipient={onClickUserGroupRecipient}
				selectedUserGroupIDs={selectedUserGroupIDs}
			/>,
		);
	}
	if (teachers.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Teachers"
				name="Teachers"
				users={teachers}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}
	if (administration.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Administration"
				name="Administration"
				users={administration}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}
	if (variant === "admin" && otherGroups.length > 0) {
		sections.push(
			<UserGroupRecipientSection
				key="Other Groups"
				name="Other Groups"
				userGroups={otherGroups}
				onClickRecipient={onClickUserGroupRecipient}
				selectedUserGroupIDs={selectedUserGroupIDs}
			/>,
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<If
				condition={
					withSelected &&
					(sections.length > 0 ||
						(selectedUserIDs?.length ?? 0) > 0 ||
						(selectedUserGroupIDs?.length ?? 0) > 0 ||
						(selectedTopicStudentIDs?.length ?? 0) > 0)
				}
			>
				<SelectRecipientSection
					users={allUsers}
					onClickUserRecipient={onClickUserRecipient}
					selectedUserIDs={selectedUserIDs}
					userGroups={allUserGroups}
					onClickUserGroupRecipient={onClickUserGroupRecipient}
					selectedUserGroupIDs={selectedUserGroupIDs}
					students={allStudents}
					onClickStudent={onClickTopicStudent}
					selectedTopicStudentIDs={selectedTopicStudentIDs}
					className={styles["selected-recipient-section"]}
				/>
			</If>
			<Conditional>
				<If condition={sections.length > 0}>{jsxJoin(sections, <Rule thickness="2px" />)}</If>
				<If condition={isLoading}>Loading...</If>
				<Else>
					<EmptyView
						IconComponent={Icon.Users}
						iconSize={48}
						primaryText="Recipients"
						secondaryText="No relevant recipients to show."
					/>
				</Else>
			</Conditional>
		</Column>
	);
}

const defaultSelectedTopicStudentIDs: Student["id"][] = [];

export type AdditionalMessageRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
	onClickUserGroupRecipient: (id: User["id"]) => void;
	selectedUserGroupIDs?: User["id"][];
	selectedTopicStudentIDs?: Student["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function AdditionalMessageRecipientSections({
	onClickUserRecipient,
	selectedUserIDs,
	selectedTopicStudentIDs = defaultSelectedTopicStudentIDs,
	className,
	style,
}: AdditionalMessageRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const userFilterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	// TODO filter to only guardians and teachers of selected students
	const { data: recipientData } = useRecipients(userFilterInput.search, userFilterInput);
	const { data: guardianTeacherData } = useStudentGuardianAndTeachersForMessagesQuery({
		variables: {
			studentIDs: selectedTopicStudentIDs,
			date: toLocalDateString(new Date()),
		},
		skip: selectedTopicStudentIDs.length === 0,
	});
	const { relatedGuardians, relatedTeachers } = React.useMemo(() => {
		const guardianIDs = guardianTeacherData?.guardianConnection.edges.map((e) => e.node.id) ?? [];
		const teacherIDs = guardianTeacherData?.employeeConnection.edges.map((e) => e.node.id) ?? [];
		return splitRecipientsByRoleForStudent(recipientData, guardianIDs, teacherIDs);
	}, [recipientData, guardianTeacherData]);

	const sections = [];
	if (relatedGuardians.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Related Parents & Guardians"
				name="Related Parents & Guardians"
				users={relatedGuardians}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}
	if (relatedTeachers.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Related Teachers"
				name="Related Teachers"
				users={relatedTeachers}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}

	if (sections.length === 0) {
		return (
			<EmptyView
				IconComponent={Icon.Users}
				iconSize={48}
				primaryText="Additional Recipients"
				secondaryText="No relevant recipients to show."
				className={className}
				style={style}
			/>
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			{jsxJoin(sections, <Rule thickness="2px" />)}
		</Column>
	);
}

const defaultSelectedUserIDs: User["id"][] = [];

export type MessageEditRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
	variant: "admin" | "teacher";
} & Partial<ClassNameProps & StyleProps>;

export function MessageEditRecipientSections({
	onClickUserRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	variant,
	className,
	style,
}: MessageEditRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const userFilterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	let finalUserFilterInput = userFilterInput;
	if (variant === "teacher") {
		finalUserFilterInput = {
			and: [userFilterInput, teacherVariantUserFilterInput],
		};
	}
	const { loading: loadingRecipients, data: recipientData } = useRecipients(
		userFilterInput.search,
		finalUserFilterInput,
	);

	const isLoading = loadingRecipients;
	const [initialSelectedUserIDs] = React.useState(selectedUserIDs);
	const users = React.useMemo(() => {
		return (
			recipientData?.userConnection.edges
				.map((e) => e.node)
				.sort(compareObjects((e) => initialSelectedUserIDs.includes(e.id), compareBooleansDesc())) ?? []
		);
	}, [recipientData, initialSelectedUserIDs]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<Conditional>
				<If condition={users.length > 0}>
					<UserRecipientList
						users={users}
						selectedUserIDs={selectedUserIDs}
						onClickUserRecipient={onClickUserRecipient}
						paginationSyncID={userFilterFormState.formValues.search}
					/>
				</If>
				<If condition={isLoading}>Loading...</If>
				<Else>
					<EmptyView
						IconComponent={Icon.Users}
						iconSize={48}
						primaryText="Recipients"
						secondaryText="No relevant recipients to show."
					/>
				</Else>
			</Conditional>
		</Column>
	);
}
